import type { ShoppingList } from '../types/GraphQL';
import { useBFF } from '~/composables/useBFF/useBFF';
import useUiNotification from '~/composables/useUiNotification';

const wishlist = ref<ShoppingList>();
const queueForWishlist = ref();

export const useWishlist = () => {
  const { $me } = useBFF();
  const { $flags } = useNuxtApp();
  const { send } = useUiNotification();
  const { addToWishlist: trackAddToWishlist } = useGA4();

  const load = async () => {
    try {
      if (!wishlist.value && $flags.hasnt('enabledKiosk') && $flags.has('shoppinglist.wishlist')) {
        const { data } = await $me.getWishlist();

        if (data) {
          wishlist.value = data;
        }
        else {
          const { data } = await $me.createWishlist();

          wishlist.value = data;
        }
      }
    }
    catch {
      send({
        message: 'An error occured',
        type: 'danger',
        icon: 'heart',
      });
    }
  };

  const addToWishlist = async (product: any) => {
    try {
      const { id, version } = wishlist.value as ShoppingList;
      const { sku, custom } = product;
      const { data } = await $me.addItemToWishlist(id, {
        version, sku, custom,
      });

      if (data) {
        wishlist.value = data;

        send({
          message: 'addToWishlistMessage',
          type: 'success',
          icon: 'heart_fill',
        });

        trackAddToWishlist(product);
      }
    }
    catch {
      send({
        message: 'An error occured',
        type: 'danger',
        icon: 'heart',
      });
    }
  };

  const removeFromWishlist = async (product: any) => {
    const lineItemId = getCurrentProduct(product)?.id;
    await removeLineItemFromWishlist(lineItemId);
  };

  const removeLineItemFromWishlist = async (lineItemId?: string) => {
    try {
      if (!wishlist.value || !lineItemId) {
        return;
      }

      const { id, version } = wishlist.value;
      const { data } = await $me.removeItemFromWishlist(id, lineItemId, { version });
      if (data) {
        wishlist.value = data;

        send({
          message: 'removeFromWishlistMessage',
          type: 'success',
          icon: 'heart',
        });
      }
    }
    catch {
      send({
        message: 'An error occured',
        type: 'danger',
        icon: 'heart',
      });
    }
  };

  const toggleToWishlist = async (product: any, onlyAddToWishlist = false) => {
    const productInWishlist = getCurrentProduct(product);
    if (productInWishlist && !onlyAddToWishlist) {
      await removeLineItemFromWishlist(productInWishlist.id);
    }
    else {
      await addToWishlist(product);
    }
  };

  const getCurrentProduct = (product: any) => {
    let currentColor: string = '';
    if (product.variant) {
      currentColor = product.variant?.attributes?.color;
    }
    else if (product.variants) {
      currentColor = product.variants?.find(variant => variant.sku === product.sku)?.attributes?.color;
    }
    else {
      currentColor = product.attributes?.color;
    }
    return wishlist.value?.lineItems.find(lineItem => lineItem.productId === product.id && lineItem.variant?.attributes?.color === currentColor);
  };

  const isInWishlist = (product: any) => !!getCurrentProduct(product);
  const clearWishlist = () => {
    wishlist.value = undefined;
  };
  const addToQueue = (product: any) => {
    queueForWishlist.value = product;
  };

  // Rethink this otherwise we be getting circular dependency with useUser
  const addToWishlistAfterAuth = async () => {
    if (queueForWishlist.value) {
      await load();
      await toggleToWishlist(queueForWishlist.value, true);
      queueForWishlist.value = null;
    }
  };

  return {
    addToWishlistAfterAuth,
    load,
    addToQueue,
    wishlist: computed(() => wishlist.value),
    wishlistTotalItems: computed(() => wishlist.value?.lineItems?.length ?? 0),
    toggleToWishlist,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    clearWishlist,
  };
};
